import { CONFIG } from 'src/config';
import { capitalize } from 'src/utils/capitalize';

import { addAuthHeaders } from '../addAuthHeaders';
import { catchHttpError, onStatus } from '../catchHttpError';
import { createApiClient } from '../createApiClient';
import { HTTPError } from '../createApiClient.types';
import { RetryException } from '../queries/types';
import { removeUndefinedFields } from '../removeUndefinedFields';
import { setTectonicDomain } from '../setTectonicDomain';
import type {
  DevicesV1ConnectedResponse,
  IntegrationListResponse,
  IntegrationTermsOfServiceStatusResponse,
  IntegrationToSave,
  PasscodeResetsPendingResponse,
  ProfileV1ConnectedResponse,
  ProviderDetails,
  ProviderType,
} from './cloudApi.types';
import { IntegrationException } from './cloudApi.types';

// =============== API Client =================

const cloudApi = createApiClient({
  prefixUrl: CONFIG.CloudApiUrl,
  hooks: { beforeRequest: [addAuthHeaders, setTectonicDomain] },
});

// PASSCODE RESET ------------------------------

export const getPasscodeResetRequests = async () =>
  cloudApi
    .get('/passcode/v1/resets?pending')
    .json<PasscodeResetsPendingResponse>();

export const passcodeResetApprove = async (requestId: string) =>
  cloudApi
    .post(`/passcode/v1/resets/${requestId}/approve`)
    .json<PasscodeResetsPendingResponse>();

export const passcodeResetDeny = async (requestId: string) =>
  cloudApi
    .post(`/passcode/v1/resets/${requestId}/deny`)
    .json<PasscodeResetsPendingResponse>();

// DEVICE --------------------------------------

// DEVICE V1 --------------------------------------

export const getDevicesV1 = async () =>
  cloudApi
    .get('/devices/v1?includeDeleted')
    .json<DevicesV1ConnectedResponse>()
    .catch((error) => {
      if (error instanceof HTTPError) {
        throw error;
      }

      if (error instanceof Error) {
        throw new RetryException(error.message, 3);
      }

      throw error;
    });

export const getActiveDevicesV1 = async () =>
  cloudApi.get('/devices/v1').json<DevicesV1ConnectedResponse>();

export const deleteDeviceV1 = async (deviceId: string, modified: string) =>
  cloudApi
    .delete('/devices/v1', {
      json: { DeviceID: deviceId, Modified: modified },
    })
    .text();

// PROFILE DEVICE V1 --------------------------------------

export const getProfileV1 = async () =>
  catchHttpError(
    cloudApi.get('/profile/v1').json<ProfileV1ConnectedResponse>(),
    onStatus('TooManyRequests', {
      devices: [],
      isTabletOwner: false,
    })
  );

export const deleteProfileDeviceV1 = async (deviceId: string) => {
  await cloudApi.delete(`/profile/v1/devices/${deviceId}`);
};

// INTEGRATIONS -------------------------------

export const getProviders = async () =>
  cloudApi
    .get('/integrations/v2/providers')
    // Backend is moving providerType from lowercase to capitalized; accept both until it ships
    .json<
      (Omit<ProviderDetails, 'providerType'> & { providerType: string })[]
    >()
    .then((providers) =>
      providers.map((provider): ProviderDetails => ({
        ...provider,
        providerType: capitalize(provider.providerType) as ProviderType,
      }))
    );

export const getIntegrations = async () =>
  cloudApi
    .get('/integrations/v2/instances')
    .json<IntegrationListResponse>()
    .then((res) => res.integrations);

export const getAddIntegrationAuthURI = async (vars: {
  providerPath: string;
}) =>
  cloudApi.get(`/integrations/v2/auth/${vars.providerPath}/authcode`).text();

export const getIntegrationReauthURI = async (vars: {
  providerPath: string;
  integrationId: string;
}) =>
  cloudApi
    .get(`/integrations/v2/auth/${vars.providerPath}/authcode`, {
      searchParams: { integrationId: vars.integrationId },
    })
    .text();

export const deleteIntegration = async (integrationId: string) =>
  cloudApi.delete(`/integrations/v2/instances/${integrationId}`).text();

export const saveIntegration = async (vars: {
  integration: IntegrationToSave;
  providerPath: string;
}) =>
  catchHttpError(
    cloudApi
      .post(`/integrations/v2/auth/${vars.providerPath}/callback`, {
        searchParams: removeUndefinedFields(vars.integration),
      })
      .text(),
    onStatus(409, async (error) => {
      // `message` is a JSON-encoded problem details object; match on its stable `type` URL
      const data = (await error.response.json()) as { message?: string };

      if (data.message?.includes('/integration-already-added')) {
        throw new IntegrationException('ALREADY_ADDED');
      }

      // Reauthorizing with a different account than the original integration
      if (data.message?.includes('/patch-integration-conflict')) {
        throw new IntegrationException('ACCOUNT_MISMATCH');
      }
    })
  );

export const updateIntegration = async (id: string, name: string) =>
  catchHttpError(
    cloudApi.patch(`/integrations/v2/instances/${id}`, { json: { name } })
  );

export const getIntegrationTermsOfServiceStatus = async () =>
  cloudApi
    .get('/integrations/v2/admin/tos')
    .json<IntegrationTermsOfServiceStatusResponse>();

export const acceptIntegrationTermsOfService = async ({
  version,
}: {
  version: number;
}) =>
  cloudApi
    .put('/integrations/v2/admin/tos', {
      searchParams: { tosVersion: version },
    })
    .json<IntegrationTermsOfServiceStatusResponse>();

// OTP
export const getOneTimeCode = async () => cloudApi.post('/devices/v1').text();
